/* eslint-disable max-classes-per-file*/
import { PresentationType } from '~/converters/presentation-type';
import { ATORequest } from '~/forms/ato-interfaces/models/ato-request';
import { ATOResponse } from '~/forms/ato-interfaces/models/ato-response';
import { CodeAndDescription } from '~/models/code-and-description';
import { ColumnDefinitionMap } from '~/models/column-definition';
import { FieldModel } from '~/models/form-model';

/**
 * Converts a {@link CodeAndDescription} object into a string. The presentationType parameter
 * is used if specified, otherwise PresentationType.PRESENTATION is used by default.
 * Currently used for {@link Select} and {@link Multiselect} components to render their dropdown options
 * @param {CodeAndDescription}	codeAndDescription		The code and description to be converted
 * @param {PresentationType}	presentationType		Optional presentationType
 */
export class CodeAndDescriptionPairValueConverter {
	public toView(
		codeAndDescription: CodeAndDescription,
		presentationType: PresentationType = PresentationType.PRESENTATION,
	): string {
		if (
			presentationType === PresentationType.PRESENTATION &&
			codeAndDescription &&
			codeAndDescription.code
		) {
			return (
				codeAndDescription.code +
				' (' +
				codeAndDescription.description +
				')'
			);
		} else if (
			presentationType === PresentationType.CODE &&
			codeAndDescription &&
			codeAndDescription.code
		) {
			return codeAndDescription.code;
		}
		return codeAndDescription.description;
	}
}

// tslint:disable:max-classes-per-file
/**
 * Converts a field on an {@link ATORequest} or {@link ATOResponse} object to PresentationType.PRESENTATION
 * using a {@link ColumnDefinitionMap} to provide the codeAndDescriptionList
 * Currently used for fields in the drilldown dialog on {@link ATOInterfaces}
 * @param {ATORequest | ATOResponse}	model				The ATORequest or ATOResponse model object
 * @param {ColumnDefinitionMap}			columnDefinitions	The source of the codeAndDescriptionLists
 * @param {string}						fieldName			The name of the field to be converted
 */
export class ModelAndColumnDefinitionsToDescriptionValueConverter {
	public toView(
		model: ATORequest | ATOResponse,
		columnDefinitions: ColumnDefinitionMap,
		fieldName: string,
	) {
		if (!model) {
			return '';
		}

		const value = model[fieldName];
		const codeAndDescriptionList = columnDefinitions[fieldName].codeList;

		return codeAndDescriptionToValue(value, codeAndDescriptionList);
	}
}

/**
 * Converts the fieldModel of a code and description field into a string. The presentationType
 * parameter is used if specified, otherwise the presentationType on the fieldModel is used.
 * If both of these are undefined, PresentationType.PRESENTATION is used by default.
 * Currently used for {@link ReadOnlyField} and {@link ATOInterfaces} STIC requests
 * @param {FieldModel}			column				The fieldModel of the field to be converted
 * @param {PresentationType}	presentationType	Optional presentationType
 */
export class ColumnCodeToDescriptionValueConverter {
	public toView(column: FieldModel, presentationType?: PresentationType) {
		if (!column || !column.value || column.value.trim().length === 0) {
			return '';
		}

		// NOTE(Albert): 2020-04-24 - WEBUI-2867
		// This logic matches the Java method
		// au.com.finsyn.acuritywebserver.core.dao.CodesAndDescriptionsDao.java#useDescriptionsForCodes
		// Swap code with descriptions if field length is > 2 (+1 for null terminator)
		// eg D2z_Title, COz_Reporting_Centre as description (rather than code) is stored in database
		const useDescriptionsForCodes: boolean =
			column.length && column.length > 3;
		return codeAndDescriptionToValue(
			column.value,
			column.codeList,
			presentationType ? presentationType : column.presentationType,
			useDescriptionsForCodes,
		);
	}
}

function codeAndDescriptionToValue(
	value: string,
	codeAndDescriptionList: CodeAndDescription[],
	convertTo: PresentationType = PresentationType.PRESENTATION,
	useDescriptionsForCodes = false,
): string {
	const codeListKey = useDescriptionsForCodes ? 'description' : 'code';

	for (const i in codeAndDescriptionList) {
		if (codeAndDescriptionList[i][codeListKey] === value) {
			return presentCodeAndDescription(
				codeAndDescriptionList[i],
				convertTo,
			);
		}
	}
	return '';
}

function presentCodeAndDescription(
	codeAndDescription: CodeAndDescription,
	convertTo: PresentationType,
) {
	switch (convertTo) {
		case PresentationType.CODE:
			return codeAndDescription.code;
		case PresentationType.DESCRIPTION:
			return codeAndDescription.description;
		case PresentationType.PRESENTATION:
			return (
				codeAndDescription.code +
				' (' +
				codeAndDescription.description +
				')'
			);
		default:
			const exhaustiveCheck: never = convertTo;
			throw new Error('Unrecognised Presentation Type: ' + convertTo);
	}
}
